import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useEscritorio } from '../../contexts/EscritorioContext'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useAuth } from '../../hooks/useAuth'
import { useAlertas } from '../../hooks/useAlertas'
import {
  type AppUser, type NavItem, type UserSignOutProps,
  NAV_MAIN, NAV_SECONDARY, UserAvatar, TopbarMode, filterNavByPermission,
} from './SidebarTopbar'

// ─── Tipos locais ─────────────────────────────────────────
type NavMode = 'mobile' | 'topbar' | 'sidebar'

interface SidebarProps {
  user: AppUser | null
  onSignOut: () => void
  mode: NavMode
}

interface SideNavItemProps extends NavItem {
  expanded: boolean
  badge?: number
}

interface SheetNavItemProps extends NavItem {
  onClose: () => void
  badge?: number
}

const LBL_CLS = 'text-[9px] font-bold uppercase tracking-[0.12em] whitespace-nowrap'

// ─── SideNavItem ──────────────────────────────────────────
function SideNavItem({ to, icon, label, expanded, badge }: SideNavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => [
        'rounded-lg transition-colors duration-150 flex items-center p-2.5 relative',
        expanded ? 'justify-start gap-3' : 'justify-center gap-0',
        isActive
          ? 'text-[var(--color-hero)] bg-[var(--color-surface)]'
          : 'text-[var(--color-muted)] hover:text-white hover:bg-[var(--color-surface)]',
      ].join(' ')}
    >
      <span className="material-symbols-outlined text-[22px] shrink-0">{icon}</span>
      {expanded && <span className={LBL_CLS}>{label}</span>}
      {!!badge && badge > 0 && (
        <span className="absolute top-1 right-1 bg-[var(--color-vermelho)] text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  )
}

function getExpandedInit(): boolean {
  try { return localStorage.getItem('sidebar_expanded') === 'true' } catch { return false }
}

// ─── SidebarMode ─────────────────────────────────────────
function SidebarMode({ user, onSignOut }: UserSignOutProps) {
  const [expanded, setExpanded] = useState<boolean>(getExpandedInit)
  const { canShowroom, can, projetoScope } = useAuth()
  const navigate = useNavigate()
  const { logoUrl } = useEscritorio()
  const { countNaoLidos: alertCount } = useAlertas({ lido: false })
  const LOGO = logoUrl ?? '/assets/images/logo-ifp.png'
  const mainFiltered = filterNavByPermission(NAV_MAIN, can, projetoScope)
  const secFiltered  = filterNavByPermission(NAV_SECONDARY, can, projetoScope)

  function toggle(): void {
    setExpanded((prev: boolean) => {
      const next = !prev
      try { localStorage.setItem('sidebar_expanded', String(next)) } catch {}
      return next
    })
  }

  return (
    <aside
      style={{ width: expanded ? 180 : 56, transition: 'width 250ms ease-in-out' }}
      className="shrink-0 flex flex-col border-r border-[var(--color-line)] bg-[var(--color-bg)] z-50 h-full"
    >
      <div
        onClick={toggle}
        title={expanded ? 'Recolher menu' : 'Expandir menu'}
        className={`h-14 shrink-0 border-b border-[var(--color-line)] cursor-pointer flex items-center px-3 ${expanded ? 'justify-between' : 'justify-center'}`}
      >
        <img src={LOGO} alt="IFP" onClick={(e) => { e.stopPropagation(); navigate('/hoje') }} className="w-9 h-auto object-contain shrink-0 cursor-pointer" />
        {expanded && <span className="material-symbols-outlined text-lg text-[#444] shrink-0">chevron_left</span>}
      </div>

      <nav className="flex flex-col gap-1 px-2 pt-4 flex-1 overflow-hidden">
        {mainFiltered.map(item => (
          <SideNavItem key={item.to} {...item} expanded={expanded} badge={item.to === '/hoje' ? alertCount : undefined} />
        ))}
        {secFiltered.length > 0 && <div className="my-3 border-t border-[var(--color-line)]" />}
        {secFiltered.map(item => <SideNavItem key={item.to} {...item} expanded={expanded} />)}
      </nav>

      <div className="flex flex-col border-t border-[var(--color-line)] px-2 py-3 overflow-hidden gap-1">
        {canShowroom && (
          <>
            <SideNavItem to="/showroom" icon="slideshow" label="Showroom" expanded={expanded} />
            <div className="mb-1 border-t border-[var(--color-line)]" />
          </>
        )}
        {user && (
          <div className="flex items-center gap-2.5 px-2.5 py-1.5 overflow-hidden">
            <div className="w-7 h-7 rounded-full shrink-0 bg-[var(--color-element)] border border-[var(--color-line)] flex items-center justify-center">
              <span className="text-[11px] font-bold text-white/55">{user.nome?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="min-w-0 transition-opacity duration-200" style={{ opacity: expanded ? 1 : 0 }}>
              <p className="text-[11px] font-bold whitespace-nowrap text-[#f0f0f0]">{user.nome}</p>
              <p className="text-[9px] text-[#555] whitespace-nowrap">{user.cargo}</p>
            </div>
          </div>
        )}
        <button
          onClick={onSignOut}
          title="Sair"
          aria-label="Sair"
          className={`text-[var(--color-muted)] hover:text-white rounded-lg transition-colors duration-150 flex items-center p-2.5 w-full bg-transparent border-none cursor-pointer ${expanded ? 'justify-start gap-3' : 'justify-center gap-0'}`}
        >
          <span className="material-symbols-outlined text-[22px] shrink-0">logout</span>
          {expanded && <span className={LBL_CLS}>Sair</span>}
        </button>
      </div>
    </aside>
  )
}

// ─── MobileMode ───────────────────────────────────────────
function SheetNavItem({ to, icon, label, onClose, badge }: SheetNavItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClose}
      className={({ isActive }) => [
        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150',
        isActive
          ? 'text-[var(--color-hero)] bg-[var(--color-surface)]'
          : 'text-[var(--color-muted)] hover:text-white hover:bg-[var(--color-surface)]',
      ].join(' ')}
    >
      <span className="material-symbols-outlined text-[22px] shrink-0">{icon}</span>
      <span className={LBL_CLS}>{label}</span>
      {!!badge && badge > 0 && (
        <span className="bg-[var(--color-vermelho)] text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 leading-none ml-auto">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  )
}

function MobileMode({ user, onSignOut }: UserSignOutProps) {
  const [open, setOpen] = useState<boolean>(false)
  const { canShowroom, can, projetoScope } = useAuth()
  const navigate = useNavigate()
  const close = (): void => setOpen(false)
  const { logoUrl } = useEscritorio()
  const { countNaoLidos: alertCount } = useAlertas({ lido: false })
  const LOGO = logoUrl ?? '/assets/images/logo-ifp.png'
  const mainFiltered = filterNavByPermission(NAV_MAIN, can, projetoScope)
  const secFiltered  = filterNavByPermission(NAV_SECONDARY, can, projetoScope)

  return (
    <header className="h-14 shrink-0 flex items-center justify-between px-4 border-b border-[var(--color-line)] bg-[var(--color-bg)] z-50">
      <img src={LOGO} alt="IFP" onClick={() => navigate('/hoje')} className="h-9 w-auto object-contain cursor-pointer" />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button aria-label="Abrir menu" className="p-2 rounded-lg text-[var(--color-muted)] hover:text-white hover:bg-[var(--color-surface)] transition-colors bg-transparent border-none cursor-pointer">
            <span className="material-symbols-outlined text-2xl">menu</span>
          </button>
        </SheetTrigger>

        <SheetContent side="left" className="w-[220px] p-0 flex flex-col border-r border-[var(--color-line)] bg-bg">
          <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
          <div className="h-14 shrink-0 flex items-center px-4 border-b border-[var(--color-line)]">
            <img src={LOGO} alt="IFP" onClick={() => { close(); navigate('/hoje') }} className="h-9 w-auto object-contain cursor-pointer" />
          </div>

          <nav className="flex flex-col gap-1 p-3 flex-1">
            {mainFiltered.map(item => (
              <SheetNavItem key={item.to} {...item} onClose={close} badge={item.to === '/hoje' ? alertCount : undefined} />
            ))}
            {secFiltered.length > 0 && <div className="my-2 border-t border-[var(--color-line)]" />}
            {secFiltered.map(item => <SheetNavItem key={item.to} {...item} onClose={close} />)}
            {canShowroom && (
              <>
                <div className="my-2 border-t border-[var(--color-line)]" />
                <SheetNavItem to="/showroom" icon="slideshow" label="Showroom" onClose={close} />
              </>
            )}
          </nav>

          <div className="border-t border-[var(--color-line)] p-3 flex flex-col gap-1">
            {user && (
              <div className="flex items-center gap-2.5 px-3 py-2">
                <div className="w-7 h-7 rounded-full shrink-0 bg-[var(--color-element)] border border-[var(--color-line)] flex items-center justify-center">
                  <span className="text-[11px] font-bold text-white/55">{user.nome?.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-[#f0f0f0] whitespace-nowrap">{user.nome}</p>
                  <p className="text-[9px] text-[#555] whitespace-nowrap">{user.cargo}</p>
                </div>
              </div>
            )}
            <button
              onClick={() => { close(); onSignOut() }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[var(--color-muted)] hover:text-white hover:bg-[var(--color-surface)] transition-colors w-full bg-transparent border-none cursor-pointer"
            >
              <span className="material-symbols-outlined text-[22px] shrink-0">logout</span>
              <span className={LBL_CLS}>Sair</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  )
}

// ─── Export ───────────────────────────────────────────────
export default function Sidebar({ user, onSignOut, mode }: SidebarProps) {
  if (mode === 'mobile') return <MobileMode user={user} onSignOut={onSignOut} />
  if (mode === 'topbar') return <TopbarMode user={user} onSignOut={onSignOut} />
  return <SidebarMode user={user} onSignOut={onSignOut} />
}
